import { useState, useEffect } from "react";
import { useAdmin } from "@/contexts/AdminContext";
import { toast } from "sonner";
import { Plus, Save, Trash2, MapPin } from "lucide-react";

const emptyAddr = { office_name: "", address_line1: "", address_line2: "", city: "", country: "", phone: "", email: "", show_in_footer: true, show_in_contact: true, order: 0 };

export default function AdminAddresses() {
  const { api } = useAdmin();
  const [addresses, setAddresses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAddr);

  const load = () => api.get("/addresses").then(r => setAddresses(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post("/admin/addresses", form);
        toast.success("Address created");
      } else {
        await api.put(`/admin/addresses/${editing}`, form);
        toast.success("Address updated");
      }
      setEditing(null);
      setForm(emptyAddr);
      load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try { await api.delete(`/admin/addresses/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-addresses">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-2xl text-[#0A1628] font-medium">Office Addresses</h1>
        <button onClick={() => { setEditing("new"); setForm(emptyAddr); }} data-testid="add-address" className="flex items-center gap-2 bg-[#0A1628] text-white text-xs px-5 py-2.5 uppercase tracking-wider hover:bg-[#1a2c4e] rounded-[2px]">
          <Plus size={14} /> Add Address
        </button>
      </div>

      {editing && (
        <div className="bg-white border border-slate-200 p-6 rounded-[2px] mb-6" data-testid="address-form">
          <h3 className="font-heading text-lg text-[#0A1628] mb-4">{editing === "new" ? "New Address" : "Edit Address"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["office_name","address_line1","address_line2","city","country","phone","email"].map(f => (
              <div key={f}>
                <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">{f.replace(/_/g," ")}</label>
                <input type="text" value={form[f] || ""} onChange={e => setForm(s => ({...s, [f]: e.target.value}))} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px] focus:border-[#0A1628] focus:outline-none" />
              </div>
            ))}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Order</label>
              <input type="number" value={form.order} onChange={e => setForm(s => ({...s, order: parseInt(e.target.value) || 0}))} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" />
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.show_in_footer} onChange={e => setForm(s => ({...s, show_in_footer: e.target.checked}))} /> Show in Footer</label>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.show_in_contact} onChange={e => setForm(s => ({...s, show_in_contact: e.target.checked}))} /> Show in Contact</label>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={save} data-testid="save-address" className="flex items-center gap-2 bg-[#0A1628] text-white text-xs px-5 py-2.5 uppercase tracking-wider hover:bg-[#1a2c4e] rounded-[2px]"><Save size={14} /> Save</button>
            <button onClick={() => setEditing(null)} className="text-xs px-5 py-2.5 border border-slate-200 text-slate-500 uppercase tracking-wider hover:border-[#0A1628] rounded-[2px]">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {addresses.map(a => (
          <div key={a.id} className="bg-white border border-slate-200 p-5 rounded-[2px] flex items-center justify-between hover:border-[#0A1628] transition-colors">
            <div className="flex items-center gap-4">
              <MapPin size={18} className="text-[#C5A059]" />
              <div>
                <p className="text-[#0A1628] font-medium text-sm">{a.office_name}</p>
                <p className="text-slate-400 text-xs">{[a.address_line1, a.city, a.country].filter(Boolean).join(", ")}</p>
                <div className="flex gap-2 mt-1">
                  {a.show_in_footer && <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-[2px]">Footer</span>}
                  {a.show_in_contact && <span className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-[2px]">Contact</span>}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditing(a.id); setForm(a); }} className="text-xs text-slate-500 hover:text-[#0A1628] border border-slate-200 px-3 py-1.5 rounded-[2px]">Edit</button>
              <button onClick={() => remove(a.id)} className="text-xs text-red-400 hover:text-red-600 border border-slate-200 px-3 py-1.5 rounded-[2px]"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
